import React from 'react';
import { Tool } from '@/app/types/cad';
import { Button, buttonVariants } from '@/app/components/ui/button';
import { Separator } from '@/app/components/ui/separator';
import {
  MousePointer2,
  Minus,
  Pentagon,
  Type,
  Hand,
  ZoomIn,
  ZoomOut,
  Undo2,
  Redo2,
  Grid3x3,
  Menu,
  Circle,
  Waves,
  CircleDot,
  Plug,
  ArrowLeftRight,
  Magnet,
  RedoDot, // Icon für Winkel-Tool
  Squircle, // Icon für Fillet-Tool (Abrunden)
  Spline, // Icon für Hut (Zentrierter Bogen)
  Music3, // Icon für Tangentialbogen
  Settings2, // Icon für Eigenschaften
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/app/components/ui/tooltip';
import { useLanguage } from '@/app/contexts/LanguageContext';
import { cn } from '@/app/components/ui/utils';

interface ToolbarProps {
  currentTool: Tool;
  onToolChange: (tool: Tool) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onToggleDrawer: () => void;
  canUndo: boolean;
  canRedo: boolean;
  showGrid: boolean;
  onToggleGrid: () => void;
  snapEnabled: boolean;
  onToggleSnap: () => void;
  zoom: number;
  iconSize?: number; // Optional icon size in pixels (default: 20)
  trimLines?: boolean;
  onToggleTrimLines?: () => void;
  showProperties?: boolean;
  onToggleProperties?: () => void;
}

export function Toolbar({
  currentTool,
  onToolChange,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  onUndo,
  onRedo,
  onToggleDrawer,
  canUndo,
  canRedo,
  showGrid,
  onToggleGrid,
  snapEnabled,
  onToggleSnap,
  zoom,
  iconSize = 20,
  trimLines = false,
  onToggleTrimLines,
  showProperties = true,
  onToggleProperties,
}: ToolbarProps) {
  const { t } = useLanguage();

  const getButtonStyle = (variant: 'default' | 'ghost', size: 'sm') => {
    return cn(buttonVariants({ variant, size }));
  };

  return (
    <TooltipProvider>
      <div
        className="flex items-center gap-3 px-4 py-3 overflow-x-auto no-scrollbar"
        style={{
          backgroundColor: 'var(--md-surface-container)',
          boxShadow: 'var(--md-elevation-2)',
        }}
      >
        {/* Hamburger Menu */}
        <Tooltip>
          <TooltipTrigger
            onClick={onToggleDrawer}
            className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), "rounded-full w-10 h-10 p-0")}
          >
            <Menu style={{ width: iconSize, height: iconSize }} />
          </TooltipTrigger>
          <TooltipContent>{t('editor.actions.openMenu')}</TooltipContent>
        </Tooltip>

        {/* App Title */}
        <h1 className="text-lg font-medium mr-4" style={{ color: 'var(--md-on-surface)' }}>
          IsoCAD
        </h1>

        <Separator orientation="vertical" className="h-8" />

        {/* Undo/Redo */}
        <div className="flex gap-1">
          <Tooltip>
            <TooltipTrigger
              onClick={onUndo}
              disabled={!canUndo}
              className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), "rounded-full w-10 h-10 p-0")}
            >
              <Undo2 style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.actions.undo')} (Strg+Z)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={onRedo}
              disabled={!canRedo}
              className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), "rounded-full w-10 h-10 p-0")}
            >
              <Redo2 style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.actions.redo')} (Strg+Y)</TooltipContent>
          </Tooltip>
        </div>

        <Separator orientation="vertical" className="h-8" />

        {/* Drawing tools */}
        <div className="flex gap-1">
          <Tooltip>
            <TooltipTrigger
              onClick={() => onToolChange('select')}
              className={cn(buttonVariants({ variant: currentTool === 'select' ? 'default' : 'ghost', size: 'sm' }))}
            >
              <MousePointer2 style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.select')} (V)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={() => onToolChange('line')}
              className={cn(buttonVariants({ variant: currentTool === 'line' ? 'default' : 'ghost', size: 'sm' }))}
            >
              <Minus style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.line')} (L)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={() => onToolChange('shape')}
              className={cn(buttonVariants({ variant: currentTool === 'shape' ? 'default' : 'ghost', size: 'sm' }))}
            >
              <Pentagon style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.shape')} (P)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={() => onToolChange('text')}
              className={cn(buttonVariants({ variant: currentTool === 'text' ? 'default' : 'ghost', size: 'sm' }))}
            >
              <Type style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.text')} (T)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={() => onToolChange('circle')}
              className={cn(buttonVariants({ variant: currentTool === 'circle' ? 'default' : 'ghost', size: 'sm' }))}
            >
              <Circle style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.circle')} (C)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={() => onToolChange('arc')}
              className={cn(buttonVariants({ variant: currentTool === 'arc' ? 'default' : 'ghost', size: 'sm' }))}
            >
              <CircleDot style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.arc')} (A)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={() => onToolChange('spline')}
              className={cn(buttonVariants({ variant: currentTool === 'spline' ? 'default' : 'ghost', size: 'sm' }))}
            >
              <Waves style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.spline')} (S)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={() => onToolChange('connection')}
              className={cn(buttonVariants({ variant: currentTool === 'connection' ? 'default' : 'ghost', size: 'sm' }))}
            >
              <Plug style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.connection')} (K)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={() => onToolChange('dimension')}
              className={cn(buttonVariants({ variant: currentTool === 'dimension' ? 'default' : 'ghost', size: 'sm' }))}
            >
              <ArrowLeftRight style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.dimension')} (D)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={() => onToolChange('pan')}
              className={cn(buttonVariants({ variant: currentTool === 'pan' ? 'default' : 'ghost', size: 'sm' }))}
            >
              <Hand style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.pan')} (H)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={() => onToolChange('angle-dimension')}
              className={cn(buttonVariants({ variant: currentTool === 'angle-dimension' ? 'default' : 'ghost', size: 'sm' }))}
            >
              <RedoDot style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.angle')} (W)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={() => onToolChange('fillet')}
              className={cn(buttonVariants({ variant: currentTool === 'fillet' ? 'default' : 'ghost', size: 'sm' }))}
            >
              <Squircle style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.fillet')} (F)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={() => onToolChange('tangent-arc')}
              className={cn(buttonVariants({ variant: currentTool === 'tangent-arc' ? 'default' : 'ghost', size: 'sm' }))}
            >
              <Music3 style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.tangentArc')}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={() => onToolChange('centered-arc')}
              className={cn(buttonVariants({ variant: currentTool === 'centered-arc' ? 'default' : 'ghost', size: 'sm' }))}
            >
              <Spline style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.tools.centeredArc')}</TooltipContent>
          </Tooltip>
        </div>

        {/* Tool Options */}
        {currentTool === 'fillet' && onToggleTrimLines && (
          <>
            <Separator orientation="vertical" className="h-8" />
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none font-medium px-2 py-1 rounded hover:bg-black/5" style={{ color: 'var(--md-on-surface)' }}>
                <input 
                  type="checkbox" 
                  checked={trimLines} 
                  onChange={onToggleTrimLines}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                {t('editor.actions.trim')}
              </label>
            </div>
          </>
        )}

        <Separator orientation="vertical" className="h-8" />

        {/* View controls */}
        <div className="flex gap-1">
          <Tooltip>
            <TooltipTrigger
              onClick={onZoomIn}
              className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }))}
            >
              <ZoomIn style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.view.zoomIn')} (+)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={onZoomReset}
              className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }))}
            >
              <span className="text-xs font-medium">{Math.round(zoom * 100)}%</span>
            </TooltipTrigger>
            <TooltipContent>{t('editor.view.zoomReset')} (0)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={onZoomOut}
              className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }))}
            >
              <ZoomOut style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.view.zoomOut')} (-)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={onToggleGrid}
              className={cn(buttonVariants({ variant: showGrid ? 'default' : 'ghost', size: 'sm' }))}
            >
              <Grid3x3 style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.view.toggleGrid')} (G)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger
              onClick={onToggleSnap}
              className={cn(buttonVariants({ variant: snapEnabled ? 'default' : 'ghost', size: 'sm' }))}
            >
              <Magnet style={{ width: iconSize, height: iconSize }} />
            </TooltipTrigger>
            <TooltipContent>{t('editor.view.toggleSnap')}</TooltipContent>
          </Tooltip>
        </div>

        {onToggleProperties && (
          <>
            <Separator orientation="vertical" className="h-8" />
            <Tooltip>
              <TooltipTrigger
                onClick={onToggleProperties}
                className={cn(buttonVariants({ variant: showProperties ? 'default' : 'ghost', size: 'sm' }))}
              >
                <Settings2 style={{ width: iconSize, height: iconSize }} />
              </TooltipTrigger>
              <TooltipContent>{showProperties ? 'Eigenschaften ausblenden' : 'Eigenschaften anzeigen'}</TooltipContent>
            </Tooltip>
          </>
        )}
      </div>
    </TooltipProvider>
  );
}
